// angular imports
import { Component, Injector, OnChanges, SimpleChanges } from '@angular/core';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';

// cc-common imports
import { Utils } from 'cc-common';

// component imports
import { MPBaseWorkflowApprovalComponent } from '../../_base/mp-base-workflow-approval-component';

// service imports
import { WorkflowApprovedStepService } from '../../_services/workflow-approved-step.service';

// model imports
import { WFEBankAccountResultDto } from '../../../shared-workflow-module/_models/wfe-bank-account-result-dto';
import { WFEEditorParameterDto } from '../../../shared-workflow-module/_models/wfe-editor-parameter-dto';
import { WFEApprovedStepEditorResultDto } from '../../_models/workflow-approved-step/wfe-approved-step-editor-result-dto';

// constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';
import { WorkflowEditorActionConstants } from '../../../shared-workflow-module/_constants/workflow-editor-action-constants';

@Component({
    selector: 'app-workflow-approved-step',
    templateUrl: './workflow-approved-step.component.html',
    styleUrls: ['./workflow-approved-step.component.scss']
})
export class WorkflowApprovedStepComponent extends MPBaseWorkflowApprovalComponent implements OnChanges {
    approvedEditor: WFEApprovedStepEditorResultDto;

    selectedBankAccount: WFEBankAccountResultDto;
    description: string;

    constructor(
        protected readonly injector: Injector,
        private readonly workflowApprovedService: WorkflowApprovedStepService
    ) {
        super(injector);
        this.workflowStep = WorkflowConstants.APPROVED;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (Utils.notNullAndDefined(changes.workflowId) && Utils.notNullAndDefined(changes.workflowId.currentValue)) {
            this.workflowId = changes.workflowId.currentValue;
        }

        if (Utils.notNullAndDefined(changes.communityId) && Utils.notNullAndDefined(changes.communityId.currentValue)) {
            this.communityId = changes.communityId.currentValue;
        }

        if (Utils.notNullAndDefined(this.workflowId) && Utils.notNullAndDefined(this.communityId)) {
            this.resetWorkflow();
            setTimeout(() => {
                this.loadApprovedStep();
            }, 250);
        }
    }

    onApprovedStepWorkflowClose(): void {
        this.closeWorkflowApprovedStep();
    }

    onDescriptionUpdate(desc: string): void {
        this.description = desc;
    }

    onApprovedReclassifyClick(): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            this.reclassBill(this.workflowStep);
        }
    }

    onBankAccountChange(selectedBankAccount: WFEBankAccountResultDto): void {
        super.populateBankBalance(selectedBankAccount.bankAccountId);
        this.selectedBankAccount = selectedBankAccount;
    }

    onApprovedUpdateClick(): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            this.updateBill(this.workflowStep);
        }
    }

    onDeleteClick(): void {
        this.confirmBillAction(WorkflowEditorActionConstants.DELETE_ACTION);
    }

    onReverseClick(): void {
        this.confirmBillAction(WorkflowEditorActionConstants.REVERSE_ACTION);
    }

    onDenyEditorClose(): void {
        this.showBillAction = false;
    }

    onDenyReturnToCAM(): void {
        this.loadingVisible = true;
        this.workflowApprovedService.denyReturnToCAM(this.selectedBillParams, this.selectedBillAction).subscribe(
            (result: any) => {
                this.showSuccessMessageAndCloseEditor(`This bill has been ${this.selectedBillAction}d successfully.`);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onDenyReturnToAAP(): void {
        this.loadingVisible = true;
        this.workflowApprovedService.denyReturnToAAP(this.selectedBillParams, this.selectedBillAction).subscribe(
            (result: any) => {
                this.showSuccessMessageAndCloseEditor(`This bill has been ${this.selectedBillAction}d successfully.`);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onDeniedCompleteWorkflow(): void {
        this.loadingVisible = true;
        this.workflowApprovedService.deniedCompleteWorkflow(this.selectedBillParams, this.selectedBillAction).subscribe(
            (result: any) => {
                this.showSuccessMessageAndCloseEditor(`This bill has been ${this.selectedBillAction}d successfully.`);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    onForceRefresh(forceRefresh: boolean): void {
        if (Utils.isTrue(forceRefresh)) {
            this.loadApprovedStep(forceRefresh);
        }
    }

    private confirmBillAction(action: string): void {
        const customDialog = custom({
            title: 'Confirmation',
            messageHtml: `Are you sure you want to ${action.toUpperCase()} the bill?`,
            buttons: [
                {
                    text: 'Yes',
                    type: 'default',
                    onClick: () => {
                        this.selectedBillAction = action;

                        this.selectedBillParams.communityId = this.workflowEditor.communityId;
                        this.selectedBillParams.workflowId = this.workflowEditor.workflowId;

                        this.showBillAction = true;
                    }
                },
                {
                    text: 'No',
                    type: 'default'
                }
            ]
        });
        customDialog.show();
    }

    private showSuccessMessageAndCloseEditor(message: string): void {
        notify(message, 'success', MessageInterval.successMsgInterval);
        this.closeOrExitWorkflowEditor(true);
    }

    private updateWorkflowEditorChanges(): void {
        const workflowInvoiceHeaderDetail = this.invoiceHeaderDetailEditor.getWorkflowEditor();
        this.workflowEditor.postDate = workflowInvoiceHeaderDetail.postDate;
        this.workflowEditor.isEFTAccount = workflowInvoiceHeaderDetail.isEFTAccount;
        this.workflowEditor.lateFee = workflowInvoiceHeaderDetail.lateFee;
        this.workflowEditor.specialHandlingId = workflowInvoiceHeaderDetail.specialHandlingId;
        this.workflowEditor.boardApprovalRequired = workflowInvoiceHeaderDetail.boardApprovalRequired;
    }

    private prepareWorkflowLineItems(): void {
        this.isValidWorkflowToApprove = this.workflowLineItemEditor.isValidLineItems();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            const modifiedLineItems = this.workflowLineItemEditor.getModifiedLineItems();
            this.checkIfTargetCommunityAvailable(modifiedLineItems);
            this.workflowEditor.items = modifiedLineItems;
        }
    }

    private loadApprovedStep(clearCache = false): void {
        const baseParams: WFEEditorParameterDto = new WFEEditorParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workflowId = this.workflowId;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;

        this.workflowApprovedService.getWorflowApprovedStepEditor(baseParams).subscribe(
            (result: WFEApprovedStepEditorResultDto) => {
                this.approvedEditor = result;

                this.workflowEditor = this.approvedEditor.workflow;
                this.breadCrumbs = this.approvedEditor.wfStatusBreadcrumb;
                this.bankBalance = this.approvedEditor.bankBalance;
                this.dimensionSource = this.approvedEditor.dimensions;

                if (Utils.notNullAndDefined(this.workflowEditor.bankAccountId)) {
                    this.selectedBankAccount = this.approvedEditor.bankAccounts.find((d) =>
                        Utils.equals(d.bankAccountId, this.workflowEditor.bankAccountId)
                    );
                }

                this.getDefaultsComponentValues();
                if (Utils.equalsIgnoreCase(this.workflowEditor.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR)) {
                    this.populateCodingDefaultEditor(this.workflowStep);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
                this.closeWorkflowApprovedStep();
            }
        );
    }

    private finalizeWorkflowEditor(): void {
        this.updateWorkflowEditorChanges();
        this.prepareWorkflowLineItems();
    }

    private closeWorkflowApprovedStep(): void {
        this.resetWorkflowApproved();
        super.onWorkflowClose();
    }

    private resetWorkflowApproved(): void {
        this.approvedEditor = null;
        this.workflowEditor = null;
        this.breadCrumbs = [];
        this.workflowId = null;
        this.communityId = null;
        this.bankBalance = null;
        this.dimensionSource = [];
    }
}
